import { FormEvent, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorMessage } from '../api/client';
import { useToast } from '../contexts/ToastContext';

interface GmailAccount {
  id: string;
  email: string;
  created_at: string;
  last_polled_at: string | null;
  last_seen_uid: number | null;
}

export default function GmailAccountsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [emailField, setEmailField] = useState('');
  const [appPassword, setAppPassword] = useState('');

  const { data: accounts } = useQuery({
    queryKey: ['gmail-accounts'],
    queryFn: async () => (await api.get<GmailAccount[]>('/gmail/accounts')).data,
  });

  const create = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<GmailAccount>('/gmail/accounts', {
        email: emailField,
        app_password: appPassword,
      });
      return data;
    },
    onSuccess: (acc) => {
      qc.invalidateQueries({ queryKey: ['gmail-accounts'] });
      setShowForm(false);
      setEmailField('');
      setAppPassword('');
      toast.success(`${acc.email} conectada`);
    },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao conectar')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/gmail/accounts/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gmail-accounts'] }); toast.success('Conta removida'); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao remover')),
  });

  const test = useMutation({
    mutationFn: async (id: string) => (await api.post(`/gmail/accounts/${id}/test`)).data,
    onSuccess: (data) => toast.success(`OK: ${data.info?.email} · ${data.info?.messages_total} mensagens`),
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Teste falhou')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Contas Gmail</h2>
        {!showForm && (
          <button className="btn btn-primary text-sm" onClick={() => setShowForm(true)}>
            + Conectar Gmail
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-3">
          <h3 className="font-semibold">Conectar nova conta</h3>
          <div className="text-xs text-slate-600 bg-slate-100 rounded p-2 space-y-1">
            <div>
              1. Vá em <a className="underline" href="https://myaccount.google.com/security" target="_blank" rel="noreferrer">myaccount.google.com/security</a>
            </div>
            <div>2. Ative "Verificação em duas etapas" (se ainda não tiver).</div>
            <div>
              3. Em <a className="underline" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">App passwords</a>, gere uma nova senha pra "Maestro" e cole abaixo.
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Email</label>
            <input
              type="email"
              className="input"
              value={emailField}
              onChange={(e) => setEmailField(e.target.value)}
              placeholder="seu@gmail.com"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm mb-1">App password</label>
            <input
              type="text"
              className="input font-mono text-sm"
              value={appPassword}
              onChange={(e) => setAppPassword(e.target.value)}
              placeholder="xxxx xxxx xxxx xxxx"
              autoComplete="off"
              required
            />
            <p className="text-xs text-slate-500 mt-1">
              16 caracteres. Pode colar com ou sem espaços.
            </p>
          </div>

          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={create.isPending}>
              {create.isPending ? 'Conectando…' : 'Conectar'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setEmailField('');
                setAppPassword('');
              }}
              disabled={create.isPending}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {accounts?.length === 0 && !showForm && (
        <div className="card text-sm text-slate-600 text-center">
          Nenhuma conta conectada ainda.
        </div>
      )}

      <div className="space-y-2">
        {accounts?.map((a) => (
          <div key={a.id} className="card flex items-center justify-between">
            <div>
              <div className="font-medium">{a.email}</div>
              <div className="text-xs text-slate-500">
                conectada em {new Date(a.created_at).toLocaleString('pt-BR')}
                {a.last_polled_at && (
                  <> · último poll {new Date(a.last_polled_at).toLocaleString('pt-BR')}</>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                className="btn btn-secondary text-xs py-1"
                onClick={() => test.mutate(a.id)}
                disabled={test.isPending}
              >
                Testar
              </button>
              <button
                className="text-xs text-red-600 hover:text-red-800 px-2"
                onClick={() => {
                  if (confirm(`Desconectar ${a.email}?`)) remove.mutate(a.id);
                }}
                disabled={remove.isPending}
              >
                Remover
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
